import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Zap, Star, Shield, Lock, ArrowRight } from "lucide-react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAuth } from "../context/AuthContext";
import PaymentModal from "../components/PaymentModal";
import { PageWrapper, DarkNav, DarkFooter } from "../components/PageShell";

const PRICING_CSS = `
  @keyframes goldFloat {
    0%,100% { transform:translateY(0) rotate(0deg); opacity:0.6; }
    50%      { transform:translateY(-14px) rotate(3deg); opacity:1; }
  }
  @keyframes cardReveal {
    from { opacity:0; transform:scale(0.93) translateY(28px); }
    to   { opacity:1; transform:scale(1) translateY(0); }
  }
  @keyframes faqSlide {
    from { opacity:0; transform:translateY(-8px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes goldPulse {
    0%,100% { box-shadow:0 0 20px rgba(245,158,11,0.3), 0 0 40px rgba(245,158,11,0.1); }
    50%      { box-shadow:0 0 30px rgba(245,158,11,0.5), 0 0 60px rgba(245,158,11,0.2); }
  }
  .plan-card-0 { animation: cardReveal 0.6s 0.1s cubic-bezier(.23,1,.32,1) both; }
  .plan-card-1 { animation: cardReveal 0.6s 0.2s cubic-bezier(.23,1,.32,1) both; }
  .plan-card-2 { animation: cardReveal 0.6s 0.3s cubic-bezier(.23,1,.32,1) both; }
  .plan-card-3 { animation: cardReveal 0.6s 0.4s cubic-bezier(.23,1,.32,1) both; }
  @keyframes trustIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  .trust-item { animation: trustIn 0.5s 0.7s both; }
  @keyframes stepIn { from { opacity:0; transform:translateX(-16px); } to { opacity:1; transform:translateX(0); } }
  .step-0 { animation: stepIn 0.5s 0.5s both; }
  .step-1 { animation: stepIn 0.5s 0.65s both; }
  .step-2 { animation: stepIn 0.5s 0.8s both; }
`;

const PLANS = [
  {
    id:"plan_50", label:"Starter", calls:50, amount:300, gstTotal:354,
    color:"#818CF8", glow:"99,102,241", border:"rgba(129,140,248,0.35)",
    desc:"Try it out with small fleets",
    features:["50 live FASTag API calls","Multi-vehicle management","Real-time location updates","Share links for drivers"],
  },
  {
    id:"plan_100", label:"Standard", calls:100, amount:550, gstTotal:649,
    color:"#38BDF8", glow:"56,189,248", border:"rgba(56,189,248,0.35)",
    desc:"Best for growing logistics teams",
    features:["100 live FASTag API calls","Everything in Starter","Fleet analytics & insights","Priority email support"],
  },
  {
    id:"plan_150", label:"Growth", calls:150, amount:750, gstTotal:885,
    color:"#C084FC", glow:"192,132,252", border:"rgba(192,132,252,0.35)",
    desc:"Scale your fleet operations",
    features:["150 live FASTag API calls","Everything in Standard","AI copilot for route analysis","Advanced fleet reports"],
  },
  {
    id:"plan_200", label:"Pro", calls:200, amount:900, gstTotal:1062,
    color:"#F59E0B", glow:"245,158,11", border:"rgba(245,158,11,0.5)",
    desc:"For serious fleet operators", best:true,
    features:["200 live FASTag API calls","Everything in Growth","Top-up at ₹3.75/call after exhaustion","Dedicated onboarding support"],
  },
];

const FAQS = [
  { q:"What is a FASTag API call?", a:"One API call fetches fresh location and toll-crossing data for one vehicle from the NETC FASTag network. Each time you press 'Track' for a vehicle, it uses one call." },
  { q:"Do credits expire?", a:"No. Purchased credits remain valid as long as your account is active and in good standing. There are no monthly expiry deadlines." },
  { q:"Can I get a refund?", a:"Refund requests for unused credits may be considered within 7 days of purchase, provided no calls have been made. See our Refund Policy for full details." },
  { q:"Is GST included in the prices shown?", a:"The prices shown (₹354, ₹649, etc.) include 18% GST. A GST-compliant tax invoice is emailed to you automatically after payment." },
  { q:"What payment methods are accepted?", a:"We accept UPI, debit/credit cards, net banking, and wallets — all processed securely through our payment gateway." },
];

function PlanCard({ plan, onSelect, session, idx }) {
  const ref = useRef(null);

  function onMove(e) {
    const card = ref.current;
    if (!card) return;
    const r = card.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    card.style.transition = "box-shadow 0.08s";
    card.style.transform = `perspective(900px) rotateY(${x * 14}deg) rotateX(${-y * 11}deg) translateZ(10px)`;
    card.style.boxShadow = `${x * 22}px ${y * 22}px 60px rgba(${plan.glow},0.42)`;
  }
  function onLeave() {
    const card = ref.current;
    if (!card) return;
    card.style.transition = "transform 0.5s cubic-bezier(.23,1,.32,1), box-shadow 0.5s ease";
    card.style.transform = "perspective(900px) rotateY(0) rotateX(0) translateZ(0)";
    card.style.boxShadow = plan.best
      ? `0 0 30px rgba(${plan.glow},0.35), 0 8px 32px rgba(0,0,0,0.4)`
      : `0 4px 20px rgba(0,0,0,0.35)`;
  }
  function onEnter() {
    const card = ref.current;
    if (card) card.style.transition = "none";
  }

  function onTouchStart(e) {
    const card = ref.current;
    if (!card) return;
    const t = e.touches[0];
    const r = card.getBoundingClientRect();
    const x = (t.clientX - r.left) / r.width - 0.5;
    const y = (t.clientY - r.top) / r.height - 0.5;
    card.style.transition = "none";
    card.style.transform = `perspective(900px) rotateY(${x * 8}deg) rotateX(${-y * 6}deg) translateZ(6px)`;
  }
  function onTouchEnd() { onLeave(); }

  return (
    <div
      ref={ref}
      className={`plan-card-${idx}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onMouseEnter={onEnter}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        background: plan.best
          ? "linear-gradient(160deg, rgba(10,14,40,0.9) 0%, rgba(30,20,10,0.85) 100%)"
          : "rgba(10,14,40,0.78)",
        backdropFilter:"blur(24px)", WebkitBackdropFilter:"blur(24px)",
        border:`1px solid ${plan.border}`,
        borderRadius:20, padding:"28px 24px", position:"relative",
        display:"flex", flexDirection:"column",
        boxShadow: plan.best
          ? `0 0 30px rgba(${plan.glow},0.35), 0 8px 32px rgba(0,0,0,0.4)`
          : `0 4px 20px rgba(0,0,0,0.35)`,
        transformStyle:"preserve-3d",
        animation: plan.best ? "goldPulse 3s ease-in-out infinite" : "none",
      }}
    >
      {plan.best && (
        <div style={{
          position:"absolute", top:-14, left:"50%", transform:"translateX(-50%)",
          background:"linear-gradient(135deg,#F59E0B,#D97706)",
          color:"#fff", fontSize:10, fontWeight:900,
          padding:"5px 16px", borderRadius:99, letterSpacing:1.2, whiteSpace:"nowrap",
          display:"flex", alignItems:"center", gap:5,
          boxShadow:"0 4px 16px rgba(245,158,11,0.5)",
        }}>
          <Star size={9} fill="#fff" strokeWidth={0} /> MOST POPULAR
        </div>
      )}

      <div style={{ fontSize:11, fontWeight:800, color:plan.color, letterSpacing:1.5, textTransform:"uppercase", marginBottom:8 }}>
        {plan.label}
      </div>
      <div style={{ fontSize:12.5, color:"rgba(255,255,255,0.45)", marginBottom:22, lineHeight:1.5 }}>{plan.desc}</div>

      <div style={{ marginBottom:4 }}>
        <span style={{ fontSize:42, fontWeight:900, color:"#fff", letterSpacing:-2, lineHeight:1 }}>
          ₹{plan.gstTotal.toLocaleString("en-IN")}
        </span>
      </div>
      <div style={{ fontSize:12, color:"rgba(255,255,255,0.3)", marginBottom:6 }}>₹{plan.amount} + 18% GST · one-time</div>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:24, fontSize:13, fontWeight:700, color:plan.color }}>
        <Zap size={13} fill={plan.color} strokeWidth={0} /> {plan.calls} FASTag API calls
      </div>

      <ul style={{ listStyle:"none", padding:0, margin:"0 0 26px", display:"flex", flexDirection:"column", gap:11, flex:1 }}>
        {plan.features.map((f,i) => (
          <li key={i} style={{ display:"flex", gap:10, alignItems:"flex-start", fontSize:13, color:"rgba(255,255,255,0.72)" }}>
            <div style={{
              marginTop:2, width:16, height:16, borderRadius:"50%", flexShrink:0,
              background:`rgba(${plan.glow},0.18)`,
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>
              <Check size={9} color={plan.color} strokeWidth={3} />
            </div>
            {f}
          </li>
        ))}
      </ul>

      <button
        onClick={onSelect}
        style={{
          width:"100%", padding:"13px 0",
          background: plan.best
            ? "linear-gradient(135deg,#F59E0B,#D97706)"
            : `rgba(${plan.glow},0.12)`,
          border:`1.5px solid ${plan.border}`,
          borderRadius:12, cursor:"pointer",
          fontSize:14, fontWeight:700,
          color: plan.best ? "#0F172A" : plan.color,
          transition:"all 0.18s",
          letterSpacing:0.3,
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = `rgba(${plan.glow},0.3)`;
          e.currentTarget.style.color = "#fff";
          e.currentTarget.style.transform = "scale(1.02)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = plan.best ? "linear-gradient(135deg,#F59E0B,#D97706)" : `rgba(${plan.glow},0.12)`;
          e.currentTarget.style.color = plan.best ? "#0F172A" : plan.color;
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {session ? "Buy Now" : "Get Started"} →
      </button>
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { session } = useAuth();
  const px = isMobile ? 16 : 40;
  const [showPayment, setShowPayment] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    document.documentElement.classList.add("page-scrollable");
    return () => document.documentElement.classList.remove("page-scrollable");
  }, []);

  function handlePlanClick() {
    if (!session) navigate("/auth");
    else setShowPayment(true);
  }

  return (
    <PageWrapper>
      <style>{PRICING_CSS}</style>
      <DarkNav px={px} />

      {/* ── HERO ─────────────────────────────── */}
      <div style={{ position:"relative", padding:`${isMobile?48:80}px ${px}px ${isMobile?40:60}px`, textAlign:"center", overflow:"hidden" }}>
        {/* Gold ambient glow */}
        <div style={{
          position:"absolute", top:"-20%", left:"50%", transform:"translateX(-50%)",
          width:700, height:500, borderRadius:"50%",
          background:"radial-gradient(ellipse, rgba(245,158,11,0.07) 0%, transparent 70%)",
          pointerEvents:"none",
        }} />
        {/* Floating gold particles */}
        {[{top:"15%",left:"12%",d:"6s"},{top:"25%",right:"10%",d:"8s"},{top:"60%",left:"7%",d:"7s"}].map((p,i) => (
          <div key={i} style={{
            position:"absolute", ...{top:p.top, left:p.left, right:p.right},
            width:6, height:6, borderRadius:"50%",
            background:"rgba(245,158,11,0.55)",
            boxShadow:"0 0 12px rgba(245,158,11,0.8)",
            animation:`goldFloat ${p.d} ease-in-out infinite`,
            pointerEvents:"none",
          }} />
        ))}

        {/* Badge */}
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.3)",
          borderRadius:99, padding:"5px 16px", marginBottom:24,
          fontSize: isMobile ? 10 : 11, fontWeight:700, color:"#F59E0B", letterSpacing: isMobile ? 0.8 : 1.3,
          textTransform:"uppercase", textAlign:"center", maxWidth: isMobile ? 300 : "none",
        }}>
          ⚡ One-time · No subscriptions · No expiry
        </div>

        <h1 style={{
          fontSize:`clamp(32px,5.5vw,64px)`, fontWeight:900,
          margin:"0 0 18px", letterSpacing:-2, lineHeight:1.05,
          background:"linear-gradient(135deg,#fff 30%,#A5B4FC 70%,#F59E0B 100%)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
        }}>
          Pay once.<br/>Track forever.
        </h1>

        <p style={{ fontSize:"clamp(14px,1.8vw,17px)", color:"rgba(255,255,255,0.52)", margin:"0 auto 44px", maxWidth:500, lineHeight:1.75 }}>
          No subscriptions. No monthly fees. Buy API calls once and use them at your pace. Every new account gets <strong style={{color:"#A5B4FC"}}>2 free calls</strong> to start.
        </p>

        {/* Stats */}
        <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap: isMobile?16:48, flexWrap:"wrap" }}>
          {[
            { value:"2 Free", label:"calls on signup" },
            { value:"₹0", label:"monthly fee" },
            { value:"∞", label:"credit validity" },
          ].map((s,i) => (
            <div key={i} style={{ textAlign:"center" }}>
              <div style={{ fontSize:`clamp(22px,3vw,32px)`, fontWeight:900, color:"#fff", letterSpacing:-1 }}>{s.value}</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.38)", letterSpacing:0.5, marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── PLAN CARDS ─────────────────────────────── */}
      <div style={{ maxWidth:1200, margin:"0 auto", padding:`0 ${px}px` }}>
        <div style={{
          display:"grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(4,1fr)",
          gap: isMobile ? 16 : 22, alignItems:"stretch",
        }}>
          {PLANS.map((p,i) => (
            <PlanCard key={p.id} plan={p} onSelect={handlePlanClick} session={session} idx={i} />
          ))}
        </div>

        {/* Top-up note */}
        <div style={{
          marginTop:28, padding:"14px 20px",
          background:"rgba(245,158,11,0.07)", border:"1px solid rgba(245,158,11,0.25)",
          borderLeft:"3px solid #F59E0B", borderRadius:"0 14px 14px 0",
          fontSize:13.5, color:"rgba(255,215,130,0.9)", lineHeight:1.75, maxWidth:700,
          display:"flex", gap:10, alignItems:"flex-start",
        }}>
          <Star size={15} color="#F59E0B" style={{ flexShrink:0, marginTop:2 }} />
          <span><strong>Top-up perk:</strong> After exhausting any plan, top up at just ₹3.75/call + 18% GST — no need to repurchase a full plan.</span>
        </div>

        {/* ── TRUST BAR ── */}
        <div style={{
          marginTop:44, display:"flex", justifyContent:"center", alignItems:"center",
          gap: isMobile?16:36, flexWrap:"wrap",
          borderTop:"1px solid rgba(255,255,255,0.06)",
          borderBottom:"1px solid rgba(255,255,255,0.06)",
          padding:"20px 0",
        }}>
          {[
            { icon:<Shield size={16} color="#818CF8"/>, text:"SSL Secured" },
            { icon:<Lock size={16} color="#818CF8"/>, text:"Secure Payments" },
            { icon:<span style={{fontSize:15}}>🇮🇳</span>, text:"India Registered" },
            { icon:<span style={{fontSize:14,fontWeight:700,color:"#818CF8"}}>GST</span>, text:"Tax Invoices Included" },
          ].map((t,i) => (
            <div key={i} className="trust-item" style={{ display:"flex", alignItems:"center", gap:7, fontSize:12.5, color:"rgba(255,255,255,0.45)", fontWeight:500 }}>
              {t.icon} {t.text}
            </div>
          ))}
        </div>

        {/* ── HOW IT WORKS ── */}
        <div style={{ marginTop:64 }}>
          <h2 style={{
            fontSize:11, fontWeight:800, color:"rgba(165,180,252,0.6)",
            letterSpacing:2, textTransform:"uppercase", marginBottom:32, textAlign:"center",
          }}>How it works</h2>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"center", gap: isMobile ? 0 : 0, flexDirection: isMobile ? "column" : "row", flexWrap: "nowrap" }}>
            {[
              { num:"01", title:"Sign up free", desc:"Create your account. No credit card required. Get 2 API calls to test live tracking immediately." },
              { num:"02", title:"Choose a plan", desc:"Pick the plan that suits your fleet size. Pay once — credits never expire while your account is active." },
              { num:"03", title:"Track live", desc:"Your quota activates instantly after payment. Add vehicles and start tracking FASTag crossings in real time." },
            ].map((s,i) => (
              <div key={i} style={{
                flex: isMobile ? "none" : 1,
                width: isMobile ? "100%" : "auto",
                display:"flex", alignItems:"flex-start",
              }}>
                <div className={`step-${i}`} style={{
                  flex:1,
                  padding: isMobile ? "18px 0" : "0 20px",
                  borderRight: !isMobile && i < 2 ? "1px solid rgba(255,255,255,0.07)" : "none",
                  borderBottom: isMobile && i < 2 ? "1px solid rgba(255,255,255,0.06)" : "none",
                  display: isMobile ? "flex" : "block",
                  alignItems: isMobile ? "flex-start" : undefined,
                  gap: isMobile ? 18 : 0,
                }}>
                  <div style={{
                    fontSize: isMobile ? 36 : 48, fontWeight:900, lineHeight:1,
                    background:"linear-gradient(135deg,rgba(129,140,248,0.5) 0%,rgba(129,140,248,0.1) 100%)",
                    WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
                    marginBottom: isMobile ? 0 : 16, display:"block",
                    flexShrink: 0, width: isMobile ? 44 : "auto", textAlign:"center",
                  }}>{s.num}</div>
                  <div style={{ flex: isMobile ? 1 : undefined }}>
                    <div style={{ fontSize:15, fontWeight:700, color:"#fff", marginBottom:8 }}>{s.title}</div>
                    <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", lineHeight:1.75 }}>{s.desc}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── FAQ ACCORDION ── */}
        <div style={{ marginTop:64 }}>
          <h2 style={{
            fontSize:11, fontWeight:800, color:"rgba(165,180,252,0.6)",
            letterSpacing:2, textTransform:"uppercase", marginBottom:24, textAlign:"center",
          }}>Frequently asked questions</h2>
          <div style={{ maxWidth:720, margin:"0 auto", display:"flex", flexDirection:"column", gap:2 }}>
            {FAQS.map((faq,i) => (
              <div key={i} style={{
                background: openFaq===i ? "rgba(99,102,241,0.08)" : "rgba(10,14,40,0.5)",
                border:"1px solid rgba(255,255,255,0.07)",
                borderColor: openFaq===i ? "rgba(99,102,241,0.3)" : "rgba(255,255,255,0.07)",
                borderRadius:12, overflow:"hidden",
                transition:"border-color 0.2s, background 0.2s",
              }}>
                <button
                  onClick={() => setOpenFaq(openFaq===i ? null : i)}
                  style={{
                    width:"100%", textAlign:"left",
                    background:"none", border:"none", cursor:"pointer",
                    padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center",
                    fontSize:14, fontWeight:600, color: openFaq===i ? "#A5B4FC" : "rgba(255,255,255,0.8)",
                    transition:"color 0.2s",
                    gap:12,
                  }}
                >
                  {faq.q}
                  <span style={{
                    flexShrink:0, width:22, height:22, borderRadius:"50%",
                    border:`1px solid ${openFaq===i ? "rgba(99,102,241,0.5)" : "rgba(255,255,255,0.15)"}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:16, color: openFaq===i ? "#A5B4FC" : "rgba(255,255,255,0.4)",
                    fontWeight:300, lineHeight:1, transition:"all 0.2s",
                    transform: openFaq===i ? "rotate(45deg)" : "none",
                  }}>+</span>
                </button>
                <div style={{
                  maxHeight: openFaq===i ? "200px" : "0",
                  overflow:"hidden",
                  transition:"max-height 0.35s cubic-bezier(.23,1,.32,1)",
                }}>
                  <p style={{ padding:"0 20px 18px", fontSize:13.5, color:"rgba(255,255,255,0.55)", lineHeight:1.8, margin:0 }}>
                    {faq.a}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div style={{ marginTop:64, marginBottom: isMobile?40:80, textAlign:"center" }}>
          <div style={{
            position:"relative", overflow:"hidden",
            background:"linear-gradient(135deg,rgba(99,102,241,0.15) 0%,rgba(245,158,11,0.08) 100%)",
            border:"1px solid rgba(245,158,11,0.2)",
            borderRadius:24, padding:`${isMobile?32:52}px ${isMobile?20:60}px`,
          }}>
            <div style={{
              position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
              width:400, height:200, borderRadius:"50%",
              background:"radial-gradient(ellipse,rgba(245,158,11,0.06) 0%,transparent 70%)",
              pointerEvents:"none",
            }} />
            <h3 style={{ fontSize: isMobile?20:28, fontWeight:900, color:"#fff", margin:"0 0 10px", position:"relative", letterSpacing:-0.5 }}>
              Start tracking your fleet today
            </h3>
            <p style={{ fontSize:14.5, color:"rgba(255,255,255,0.5)", margin:"0 0 30px", position:"relative" }}>
              Sign up free. No credit card required.
            </p>
            <button
              onClick={() => navigate("/auth")}
              style={{
                background:"linear-gradient(135deg,#6366F1,#7C3AED)",
                border:"none", borderRadius:13, cursor:"pointer",
                fontSize:15, fontWeight:700, color:"#fff",
                padding:"14px 40px", position:"relative",
                boxShadow:"0 4px 24px rgba(99,102,241,0.45)",
                display:"inline-flex", alignItems:"center", gap:8,
                transition:"all 0.18s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform="scale(1.05)"; e.currentTarget.style.boxShadow="0 8px 36px rgba(99,102,241,0.6)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.boxShadow="0 4px 24px rgba(99,102,241,0.45)"; }}
            >
              Get Started Free <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>

      <DarkFooter px={px} />

      {showPayment && (
        <PaymentModal
          onClose={() => setShowPayment(false)}
          onSuccess={() => { setShowPayment(false); navigate("/app"); }}
          mode="plans"
        />
      )}
    </PageWrapper>
  );
}
